/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState } from 'react';
import { LocalUserChoices } from '@livekit/components-react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Logo } from '../..';
import { useSpeedTest } from '../../../hooks/useSpeedTest';
import { useSendTechCheckResultsMutation } from '../../../redux/streamGoApi';
import { Step } from '../enums';
import { StyledBox } from '../styled';
import { IConfigOption } from '../types';
import { BitrateCharts } from './BitrateCharts';
import CallQuality from './BitrateCharts/CallQuality';
import HelpVideoTiles from './HelpVideos/HelpVideoTiles';
import {
  useCalculateBitrates,
  useBrowserCompatibility,
  useIsLiveSlidesValid,
} from './hooks';
import { LiveKitTestRoom } from './LiveKitTestRoom/LiveKitTestRoom';
import { SummaryTiles } from './SummaryTiles';

const Container = styled(Box)(({ theme }) => ({
  margin: 'auto',
  borderRadius: '10px',
  padding: '15px',
  maxWidth: '1100px',
  position: 'relative',
  zIndex: 100,
  paddingBottom: '30px',
  overflow: 'hidden',
  marginBottom: '30px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'rgba(80, 127, 119, 0.06)',
}));

const Title = styled(Box)(`
  font-size: 1.5em;
  text-align: center;
  margin: 0.5em;
`);

const ResetContainer = styled(Box)(`
  text-align: center;
`);

interface IConferenceTest {
  configOptions?: IConfigOption;
  email: string;
  userChoices: LocalUserChoices;
}

export const ConferenceTest = ({
  configOptions,
  email,
  userChoices,
}: IConferenceTest) => {
  const [sendResults] = useSendTechCheckResultsMutation();
  const [connected, setConnected] = useState(false);
  const [testRunning, setTestRunning] = useState(true);
  const screenshareSuppoted =
    navigator.mediaDevices && 'getDisplayMedia' in navigator.mediaDevices;
  const { isRunning, speedDownload, speedUpload, startTest } = useSpeedTest({});
  const {
    audioBitrates,
    audioBitratesAverage,
    startBitrateTest,
    videoBitrates,
    videoBitratesAverage,
  } = useCalculateBitrates(
    userChoices.videoDeviceId,
    userChoices.audioDeviceId,
  );
  const [step, setStep] = useState<Step>(Step.SpeedTest);
  const browserDetails = useBrowserCompatibility();
  const isSlidesValid = useIsLiveSlidesValid();

  useEffect(() => {
    if (!isRunning && speedDownload && speedUpload) {
      setStep(Step.BitrateTest);
    }
  }, [isRunning, speedDownload, speedUpload]);

  useEffect(() => {
    if (step === Step.SpeedTest) {
      startTest(10);
    } else if (step === Step.BitrateTest) {
      startBitrateTest();
    }
  }, [startBitrateTest, startTest, step]);

  useEffect(() => {
    if (
      step === Step.BitrateTest &&
      videoBitrates.length === 20 &&
      audioBitrates.length === 20
    ) {
      setTestRunning(false);
    } else {
      setTestRunning(true);
    }
  }, [step, videoBitrates.length, audioBitrates.length]);

  useEffect(() => {
    if (!testRunning) {
      sendResults({
        TechCheckResults: {
          audioAverageKbps: audioBitratesAverage,
          browserInfo: {
            name: browserDetails.browserInfo.name,
            version: browserDetails.browserInfo.version,
          },
          configurationOptions: configOptions as IConfigOption,
          connectivitySuccessfull: connected,
          downloadSpeed: speedDownload,
          emailAddress: email,
          errorMessage: '',
          eventStartDate: '',
          eventTitle: '',
          name: '',
          isValidBrowser: browserDetails.isCompatible,
          liveSlidesValid: isSlidesValid.isLiveSlidesValid,
          platform: browserDetails.browserInfo.platform,
          screenshareSupported: screenshareSuppoted,
          uploadSpeed: speedUpload,
          videoAverageKbps: videoBitratesAverage,
        },
      });
    }
  }, [
    audioBitratesAverage,
    browserDetails.browserInfo.name,
    browserDetails.browserInfo.platform,
    browserDetails.browserInfo.version,
    browserDetails.isCompatible,
    configOptions,
    connected,
    email,
    isSlidesValid.isLiveSlidesValid,
    screenshareSuppoted,
    sendResults,
    speedDownload,
    speedUpload,
    testRunning,
    videoBitratesAverage,
  ]);

  return (
    <Box m={3}>
      <Container>
        <Box className="container-logo" sx={{ textAlign: 'center' }}>
          <Logo />
        </Box>
        <Title>Tech Check Report</Title>
        <LiveKitTestRoom
          connected={connected}
          setConnected={setConnected}
          userChoices={userChoices}
        />
        {testRunning ? (
          <StyledBox sx={{ textAlign: 'center' }}>
            <Typography sx={{ mb: 2 }} variant="h5">
              Tests running...
            </Typography>
            {step === Step.SpeedTest ? (
              <>
                <Box>Download speed: {speedDownload.toFixed(1)}Mbps</Box>
                <Box>Upload speed: {speedUpload.toFixed(1)}Mbps</Box>
              </>
            ) : null}
            {step === Step.BitrateTest ? (
              <BitrateCharts
                audioBitrate={audioBitrates}
                videoBitrate={videoBitrates}
              />
            ) : null}
          </StyledBox>
        ) : null}
        {!testRunning ? (
          <>
            <ResetContainer>
              <Button
                color="primary"
                onClick={() => setStep(Step.SpeedTest)}
                variant="contained"
              >
                Restart Test
              </Button>
            </ResetContainer>
            <SummaryTiles
              audioAverageKbps={audioBitratesAverage}
              audioOnly={false}
              browserInfo={{
                name: browserDetails.browserInfo.name,
                version: browserDetails.browserInfo.version,
              }}
              configurationOptions={configOptions}
              connectivitySuccessfull={connected}
              downloadSpeed={speedDownload}
              hideKey={false}
              isValidBrowser={browserDetails.isCompatible}
              liveSlidesValid={isSlidesValid.isLiveSlidesValid}
              screenshareSupported={screenshareSuppoted}
              uploadSpeed={speedUpload}
              videoAverageKbps={videoBitratesAverage}
            />
            <CallQuality
              audioBitrates={audioBitrates}
              videoBitrates={videoBitrates}
            />
            <HelpVideoTiles configurationOptions={configOptions} />
          </>
        ) : null}
      </Container>
    </Box>
  );
};
